import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { api, RSD, imgURL, cardImgURL, fmtDateTime } from "../../lib/api";
import { useAuth, startGoogleLogin } from "../../context/AuthContext";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { FooterLogo } from "../../components/BrandLogo";
import { User, ChevronRight, Search, Store, X } from "lucide-react";
import { toast } from "sonner";
// Secondary sheets are only needed after an explicit tap: they stay out of the initial bundle.
import { NotifyButton } from "../../components/NotifyButton";
import { useI18n } from "../../context/I18nContext";
import { useCity } from "../../context/CityContext";
import { CitySelector } from "../../components/CitySelector";
import { RestaurantOperationalInfo } from "../../components/RestaurantOperationalInfo";
import { useInstallMode, openInstallPrompt } from "../../lib/pwaInstall";
import { Download } from "lucide-react";
import { CompactRemaining } from "../../components/OrderCountdown";
import { useServerTick } from "../../lib/serverClock";
import { useCart } from "../../context/CartContext";
import { planReorder } from "../../lib/reorder";
import { getPosition, fmtDistance } from "../../lib/geo";
import { track, normalizeQuery, impressionObserver } from "../../lib/analytics";
import { useLoyalty } from "../../context/LoyaltyContext";
import { CoinAmount, PonesiCoinIcon } from "../../components/PonesiCoinIcon";
import { fmtCoins, COIN_PM } from "../../lib/coins";
const MyCoinsSheet = lazy(() => import("./MyCoinsSheet"));
import Loader from "../../components/Loader";
import { prefetchRoute, importRestaurantPage, importOrderTracking, prefetchRestaurant } from "../../lib/prefetch";
import { getPublic } from "../../lib/dataCache";
import BottomSheet from "../../components/BottomSheet";
import CustomerBottomNav from "../../components/CustomerBottomNav";

function ActiveOrdersList({ orders, Card }) {
  const now = useServerTick(orders.length > 0);
  return <>{orders.map((o) => Card(o, now))}</>;
}

function PushToggle() {
  return <NotifyButton variant="row" />;
}

function StatusBadge({ r }) {
  const { t } = useI18n();
  const cls = r.open_now ? (r.accepting_orders ? "bg-brand-green" : "bg-brand-orange") : "bg-slate-700";
  const label = !r.open_now ? t("badge.closed", "Zatvoreno") : r.accepting_orders ? t("badge.open", "Otvoreno") : t("badge.paused", "Pauza");
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white ${cls}`}>{label}</span>;
}

function SectionLabel({ children }) {
  return <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">{children}</p>;
}

function RestaurantCard({ r, onOpen, reorderOrderId, onQuickReorder, reordering, cardRef, loyalty, priority = 0 }) {
  const { locale, t } = useI18n();
  const go = () => {
    track("restaurant_card_click", { restaurant_id: r.id, city_id: r.city_id,
                                     from_search: sessionStorage.getItem("ponesi_from_search") === "1" });
    onOpen(r);
  };
  return (
    <div role="link" tabIndex={0} onClick={go} ref={cardRef}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      onPointerEnter={() => prefetchRestaurant(r.slug)}
      onPointerDown={() => prefetchRestaurant(r.slug)}
      onFocus={() => prefetchRestaurant(r.slug)}
      data-testid={`restaurant-card-${r.slug}`}
      className="block bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] animate-slide-up cursor-pointer transition-all hover:shadow-[0_12px_40px_rgb(0,0,0,0.10)] active:scale-[0.99]">
      <div className="relative h-40">
        {r.cover_image ? <img src={cardImgURL(r.cover_image)} alt={r.name} width="448" height="176"
          loading={priority ? "eager" : "lazy"} decoding="async" fetchPriority={priority === 1 ? "high" : "auto"}
          className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-brand-orange to-brand-red" />}
        {/* Closed restaurants get a dimmed banner only — badges and the card body stay readable. */}
        {!r.open_now && <div data-testid={`closed-overlay-${r.slug}`} className="absolute inset-0 bg-slate-950/45 pointer-events-none" />}
        <div className="absolute top-3 right-3"><StatusBadge r={r} /></div>
        {/* Promotion badges come from INDEPENDENT canonical backend flags (never inferred from
            prices). Max two: Happy Hour first, then the stronger discount signal. */}
        <div className="absolute top-3 left-3 right-24 flex flex-wrap gap-1.5">
          {r.happy_hour_active && (
            <span data-testid={`hh-tag-${r.slug}`}
              className="text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-400 text-slate-900 shadow">
              {t("badge.happy_hour", "Happy Hour")}
            </span>
          )}
          {r.store_promo_active ? (
            <span data-testid={`store-promo-tag-${r.slug}`}
              className="text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-red text-white shadow">
              -{r.store_promo_percent}% {t("badge.store_promo", "na sve")}
            </span>
          ) : r.product_discount_active && (
            <span data-testid={`discount-tag-${r.slug}`}
              className="text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-red text-white shadow">
              {t("badge.discount", "Popust")}
            </span>
          )}
        </div>
        {r.is_hiring && (
          <span data-testid={`hiring-tag-${r.slug}`}
            className="absolute bottom-3 left-3 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-blue-600 text-white shadow">
            {t("badge.hiring", "Zapošljavamo")}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2">
          <h3 className="font-display font-black text-xl flex-1 min-w-0">
            {r.name}
            {/* Canonical customer-facing loyalty signal: entitlement ACTIVE + program on. */}
            {r.loyalty_earning_allowed && (
              <PonesiCoinIcon size={20} label="Ponesi Coins" data-testid={`coins-icon-${r.slug}`}
                className="ml-1.5 align-[-0.2em]" />
            )}
          </h3>
          {reorderOrderId && (
            <button data-testid={`quick-reorder-${r.slug}`} type="button" disabled={reordering}
              title={t("qr.aria", "Poruči kao prošli put")} aria-label={t("qr.aria", "Poruči kao prošli put")}
              onClick={(e) => { e.stopPropagation(); onQuickReorder(r, reorderOrderId); }}
              className="-mt-1 -mr-1 w-11 h-11 shrink-0 flex items-center justify-center rounded-full hover:bg-brand-red/5 active:scale-95 transition-colors disabled:opacity-50">
              {reordering
                ? <span className="w-4 h-4 rounded-full border-2 border-brand-red/30 border-t-brand-red animate-spin" />
                : <img src="/reorder-heart-96.png" srcSet="/reorder-heart-96.png 1x, /reorder-heart-192.png 2x" alt="" aria-hidden="true" className="w-7 h-7 object-contain select-none pointer-events-none" />}
            </button>
          )}
        </div>
        {r.category && <p className="text-sm text-slate-500 mt-0.5">{r.category}</p>}
        {r.distance_meters != null && (
          <p data-testid={`distance-${r.slug}`} className="text-sm font-bold text-brand-red mt-0.5">
            {fmtDistance(r.distance_meters)}
          </p>
        )}
        {r.description && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{r.description}</p>}
        <RestaurantOperationalInfo restaurant={r} locale={locale} t={t} className="mt-3" />
        {r.loyalty_enabled && loyalty && loyalty.balance > 0 && (
          <div data-testid={`coin-card-summary-${r.slug}`}
            className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
            <CoinAmount value={loyalty.balance} size={17} locale={locale}
              className="font-display font-black text-sm text-slate-900" />
            {loyalty.reward_available
              ? <span className="text-xs font-bold text-brand-green">{t("coins.reward_available", "Nagrada dostupna")}</span>
              : loyalty.next_reward_coin_price
                ? <span className="text-xs font-semibold text-slate-500">
                    {t("coins.to_reward", `Još ${fmtCoins(loyalty.coins_to_next_reward || 0, locale)} do nagrade`)
                      .replace("{n}", fmtCoins(loyalty.coins_to_next_reward || 0, locale))}
                  </span>
                : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PlatformHome() {
  const navigate = useNavigate();
  const { user, logout, setUser, loading: authLoading } = useAuth();
  const { t, localePath, locale, switchLocale } = useI18n();
  const installMode = useInstallMode();
  const [restaurants, setRestaurants] = useState([]);
  const [listState, setListState] = useState("loading");
  const [myOrders, setMyOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);  const searchRef = useRef(null);
  const [coinsOpen, setCoinsOpen] = useState(false);
  const { byRestaurant: loyaltyByRestaurant, refresh: refreshLoyalty } = useLoyalty();
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  const { selectedCityId, ready: cityReady } = useCity();

  useEffect(() => { if (accountOpen && user) setPhoneInput(user.phone || ""); }, [accountOpen, user]);
  // Scroll lock for these sheets is owned by the shared BottomSheet container.
  const savePhone = async () => {
    setSavingPhone(true);
    try {
      const { data } = await api.patch("/auth/phone", { phone: phoneInput });
      setUser((u) => (u ? { ...u, phone: data.phone } : u));
      setPhoneInput(data.phone);
      toast.success(t("acct.phone_saved", "Broj telefona je sačuvan"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("acct.phone_error", "Greška pri čuvanju broja"));
    } finally { setSavingPhone(false); }
  };

  // Display-only full name for the header (never persisted, never a new profile field).
  const fullName = useMemo(() => {
    const raw = (user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "").trim();
    if (!raw || raw.includes("@")) return "";
    return raw.replace(/\s+/g, " ");
  }, [user?.name, user?.first_name, user?.last_name]);

  // Single source of truth for opening the account/profile sheet (top-left avatar + bottom nav).
  // Guests keep the existing bottom-nav behaviour: start the Google login.
  const openProfile = () => {
    if (!user) { startGoogleLogin("/"); return; }
    if (accountOpen) return;   // rapid taps must not stack a second sheet
    setOrdersOpen(false);
    setAccountOpen(true);
  };

  const load = async ({ force = false } = {}) => {
    try {
      const data = await getPublic("/restaurants", { force, onUpdate: (d) => setRestaurants(d) });
      setRestaurants(data);
      setListState("ready");
    } catch {
      // A failed BACKGROUND refresh (websocket settings_update) must keep the rendered list;
      // only an initial load or an explicit retry may show the error screen.
      setListState((s) => (s === "ready" ? "ready" : "error"));
    }
  };
  const loadMine = async () => { if (!user) return; try { const { data } = await api.get("/orders/mine"); setMyOrders(data); } catch {} };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMine(); }, [user]);
  useOrderSocket((msg) => { if (msg.type === "settings_update") { load({ force: true }); if (nearestRef.current) loadNearby({ reuseCoords: true }); } if (msg.type === "order_update" && user && msg.order?.user_id === user.user_id) { loadMine(); refreshLoyalty(); } }, { userId: user?.user_id });

  const activeOrders = myOrders.filter((o) => !["completed", "cancelled", "rejected"].includes(o.status));

  const listView = useMemo(() => {
    if (selectedCityId) return restaurants.filter((r) => r.city_id === selectedCityId);
    return restaurants;
  }, [restaurants, selectedCityId]);

  // "Najbliže": explicit, user-triggered distance sorting through the existing /restaurants/nearby
  // endpoint and lib/geo getPosition(). The manually selected city stays authoritative (the city is
  // sent to the backend, GPS never switches it), and closed restaurants keep their distance rank.
  const [nearestMode, setNearestMode] = useState(false);
  const [nearbyList, setNearbyList] = useState(null);
  const [locating, setLocating] = useState(false);
  const coordsRef = useRef(null);      // last known position: reused on refresh, no extra GPS prompt
  const nearestRef = useRef(false);    // read inside the websocket handler (avoids a stale closure)
  const nearbyReqRef = useRef(0);      // ignores late responses for a city the user already left

  const loadNearby = async ({ reuseCoords = false } = {}) => {
    const req = ++nearbyReqRef.current;
    if (!reuseCoords) setLocating(true);
    try {
      const coords = (reuseCoords && coordsRef.current) || await getPosition();
      coordsRef.current = coords;
      const { data } = await api.get("/restaurants/nearby", {
        params: { lat: coords.lat, lng: coords.lng, city_id: selectedCityId || undefined } });
      if (req !== nearbyReqRef.current) return;   // a newer request already won
      setNearbyList(data);
      setNearestMode(true);
      nearestRef.current = true;
    } catch {
      if (req !== nearbyReqRef.current) return;
      if (reuseCoords) return;   // background refresh failed: keep the current list, stay silent
      setNearestMode(false);
      nearestRef.current = false;
      setNearbyList(null);
      coordsRef.current = null;
      toast.error(t("home.nearest_denied", "Lokacija nije dostupna, prikazana je uobičajena lista."));
    } finally { if (!reuseCoords) setLocating(false); }
  };

  const toggleNearest = () => {
    if (nearestMode) {
      setNearestMode(false);
      nearestRef.current = false;
      setNearbyList(null);
      nearbyReqRef.current++;
      return;
    }
    loadNearby();
  };
  // A manual city change must never leave stale results from the previous city on screen.
  useEffect(() => {
    if (!nearestRef.current) return;
    setNearbyList(null);
    loadNearby({ reuseCoords: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCityId]);

  const openRestaurant = (r) => navigate(localePath(`/restaurant/${r.slug}`));

  // Card impressions: one per restaurant per session, only after it is genuinely on screen.
  const cardIoRef = useRef(null);
  const cardMetaRef = useRef(new Map());
  useEffect(() => {
    cardIoRef.current = impressionObserver("restaurant_card_impression", (el) => {
      const r = cardMetaRef.current.get(el);
      return r ? { restaurant_id: r.id, city_id: r.city_id, dedupe_key: r.id } : {};
    });
    return () => cardIoRef.current?.disconnect();
  }, []);
  const registerCard = (el, r) => {
    if (!el || !cardIoRef.current) return;
    cardMetaRef.current.set(el, r);
    cardIoRef.current.observe(el);
  };

  // ---- Icon-only quick reorder (latest COMPLETED order for that restaurant) ----
  const { replaceAll, items: cartItems, restaurant: cartRest } = useCart();
  const [reorderMap, setReorderMap] = useState({});
  const [reorderingId, setReorderingId] = useState(null);
  const [replaceAsk, setReplaceAsk] = useState(null);

  const loadReorderable = async () => {
    if (!user) { setReorderMap({}); return; }
    try {
      const { data } = await api.get("/orders/reorderable");
      setReorderMap(Object.fromEntries((data || []).map((x) => [x.restaurant_id, x.order_id])));
    } catch { setReorderMap({}); }
  };
  useEffect(() => { loadReorderable(); }, [user]);

  // Visibility of the quick-reorder affordance, once per restaurant per session.
  useEffect(() => {
    Object.entries(reorderMap).forEach(([restaurantId, orderId]) => {
      if (orderId) track("quick_reorder_visible", { restaurant_id: restaurantId, dedupe_key: restaurantId });
    });
  }, [reorderMap]);

  const runQuickReorder = async (r, orderId) => {
    setReorderingId(r.id);
    try {
      const [{ data: order }, { data: menu }] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/restaurants/${r.slug}/menu`),
      ]);
      const { plan, skipped, skippedNames, optionIssues } = planReorder(order, menu.products);
      if (plan.length === 0) {
        toast.error(t("qr.nothing", "Nijedna stavka iz prethodne porudžbine više nije dostupna."));
        return;
      }
      const rest = { slug: r.slug, name: r.name, id: r.id, payment_method: r.payment_method,
                     loyalty_enabled: !!r.loyalty_enabled };
      replaceAll(plan, rest, {
        pickup_type: order.pickup_type === "scheduled" ? "scheduled" : "asap",
        payment_method: order.payment_method === "card" ? "card" : "cash",
      });
      if (skipped > 0) {
        const names = (skippedNames || []).filter(Boolean);
        toast.warning(names.length === 1
          ? `${names[0]} ${t("qr.one_unavailable", "trenutno nije dostupan i nije dodat u korpu.")}`
          : names.length
            ? `${t("qr.list_unavailable", "Neki proizvodi trenutno nisu dostupni i nisu dodati u korpu:")} ${names.join(", ")}.`
            : t("qr.some_unavailable", "Neke stavke iz prethodne porudžbine više nisu dostupne."),
          { duration: 6000 });
      }
      if (optionIssues > 0) toast.warning(t("qr.option_unavailable", "Neke opcije više nisu dostupne. Proverite korpu i izaberite nove."));
      track("quick_reorder_cart_created", { restaurant_id: r.id, items: plan.length, skipped });
      try { sessionStorage.setItem("ponesi_quick_reorder_rid", r.id); } catch {}
      navigate(localePath(`/restaurant/${r.slug}?cart=1`));
    } catch {
      toast.error(t("common.error", "Greška"));
    } finally { setReorderingId(null); }
  };

  const quickReorder = (r, orderId) => {
    track("quick_reorder_clicked", { restaurant_id: r.id });
    if (cartItems.length > 0) { setReplaceAsk({ r, orderId, sameRest: cartRest?.slug === r.slug }); return; }
    runQuickReorder(r, orderId);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const q = searchQuery.trim().toLowerCase();
  const filteredList = useMemo(() => {
    // Najbliže mode: distance is authoritative (backend order), open/closed never reorders it.
    // Default mode: a stable partition puts OPEN before CLOSED and keeps the backend order inside
    // each group. Closed restaurants are never hidden.
    const base = nearestMode && nearbyList
      ? nearbyList.filter((r) => !selectedCityId || r.city_id === selectedCityId)
      : [...listView.filter((r) => r.open_now), ...listView.filter((r) => !r.open_now)];
    if (!q) return base;
    return base.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q));
  }, [listView, q, nearestMode, nearbyList, selectedCityId]);
  // Debounced search tracking: the query term itself is the dedupe key, so typing one
  // search reports one event; empty-result searches are reported separately.
  useEffect(() => {
    if (q.length < 2) return;
    const id = setTimeout(() => {
      track(filteredList.length ? "search_performed" : "search_no_results",
            { dedupe_key: q, normalized_query: normalizeQuery(q),
              query_length: q.length, results: filteredList.length });
      try { sessionStorage.setItem("ponesi_from_search", "1"); } catch {}
    }, 900);
    return () => clearTimeout(id);
  }, [q, filteredList.length]);

  // ---- Initial readiness gate -------------------------------------------------------------
  // The homepage is revealed in ONE render, only once the critical first-screen state is settled:
  // auth, city, the existing /restaurants request and the banners of the initially visible cards.
  // Nothing here issues an extra request; /orders/mine is NOT critical because an active order only
  // renders a banner on this page (it never redirects), so it must not hold the gate.
  const criticalCount = typeof window !== "undefined" && window.innerWidth >= 768 ? 4 : 3;
  const [imagesSettled, setImagesSettled] = useState(false);
  // The critical banners are taken from the list that is actually RENDERED (city filter +
  // open-first partition), never from the raw backend array.
  const criticalUrls = useMemo(
    () => filteredList.slice(0, criticalCount).map((r) => r.cover_image).filter(Boolean).map(cardImgURL),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredList, criticalCount]);
  const criticalKey = criticalUrls.join("|");
  useEffect(() => {
    // Same gating conditions as `ready`: the critical list is only final once the city filter is.
    if (listState !== "ready" || !cityReady || authLoading || imagesSettled) return;
    if (!criticalUrls.length) { setImagesSettled(true); return; }
    let cancelled = false;
    const settle = () => { if (!cancelled) setImagesSettled(true); };
    // Fail-open after 1200 ms: a slow CDN can never hold the page, the image just finishes later.
    const cap = setTimeout(settle, 1200);
    Promise.all(criticalUrls.map((src) => new Promise((res) => {
      const img = new Image();
      img.onload = () => (img.decode ? img.decode().then(res, res) : res());
      img.onerror = res;                 // error counts as settled (existing fallback renders)
      img.src = src;                     // same canonical URL as the <img>, so no second download
    }))).then(settle);
    return () => { cancelled = true; clearTimeout(cap); };   // a stale set can never open the gate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState, cityReady, authLoading, criticalKey]);

  const ready = !authLoading && cityReady && listState === "ready" && imagesSettled;
  // Warm up secondary route chunks only after the homepage is revealed and the browser is idle.
  useEffect(() => { if (ready) prefetchRoute("restaurant-page", importRestaurantPage); }, [ready]);
  useEffect(() => { if (ready && activeOrders.length) prefetchRoute("order-tracking", importOrderTracking); },
    [ready, activeOrders.length]);

  if (listState === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-[#F9F9F7]"
        data-testid="home-error">
        <FooterLogo height={64} />
        <p className="font-display font-black text-xl">{t("home.load_error", "Nije moguće učitati restorane")}</p>
        <button data-testid="home-retry" onClick={() => { setListState("loading"); load({ force: true }); }}
          className="px-5 py-3 rounded-xl bg-brand-red text-white font-bold">
          {t("common.retry", "Pokušaj ponovo")}
        </button>
      </div>
    );
  }
  if (!ready) return <Loader />;


  return (
    <div className="ponesi-customer min-h-screen bg-[#F9F9F7]">
      <div className="max-w-md mx-auto bg-[#F9F9F7] min-h-screen shadow-2xl relative pb-36">
        <header className="px-5 pt-6">
          {/* Avatar + canonical full name (display only, no extra request) and the quick notification bell. */}
          <div className="flex items-center gap-3">
            {/* Same trigger as the bottom-nav Profile action — one openProfile(), one sheet. */}
            <button type="button" onClick={openProfile} aria-label={t("acct.open_profile", "Otvori profil")}
              className="w-11 h-11 rounded-full bg-[#F6E2E2] text-brand-red flex items-center justify-center overflow-hidden shrink-0 active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              data-testid="header-avatar">
              {user?.picture
                ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                : <User size={22} strokeWidth={2.2} />}
            </button>
            <p data-testid="header-name" className="flex-1 min-w-0 truncate font-montserrat font-semibold text-[15px] text-[#232426]">
              {!authLoading && fullName ? fullName : ""}
            </p>
            <NotifyButton variant="icon" />
          </div>

          <h1 data-testid="home-headline"
            className="font-coliner font-semibold mt-4 text-[26px] sm:text-[30px] leading-[1.15] text-slate-900">
            <span className="text-brand-red">{t("home.headline_accent", "Pozdrav,")}</span>{" "}
            {t("home.headline_rest", "šta poručuješ danas?")}
          </h1>

          <div className="mt-4 bg-brand-red rounded-[18px] flex items-center gap-3 px-4 py-3.5 shadow-md" data-testid="search-bar">
            <Search size={20} className="text-white flex-shrink-0" />
            <input ref={searchRef} data-testid="search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t("home.search", "Pretraži restorane")} placeholder={t("home.search", "Pretraži restorane")}
              className="bg-transparent outline-none text-base leading-5 flex-1 min-w-0 text-white placeholder:text-white/75" />
            {searchQuery && (
              <button data-testid="search-clear" aria-label={t("home.clear_search", "Obriši pretragu")} onClick={() => setSearchQuery("")}
                className="text-white/80 hover:text-white flex-shrink-0"><X size={16} /></button>
            )}
          </div>
        </header>

        {user && activeOrders.length > 0 && (
          <button data-testid="active-order-banner" onClick={() => navigate(localePath(`/order/${activeOrders[0].id}`))}
            className="w-[calc(100%-2.5rem)] mx-5 mt-4 flex items-center justify-between bg-brand-green text-white rounded-2xl px-4 py-3 animate-slide-up">
            <span className="font-semibold text-sm">{t("home.active_order", "Aktivna porudžbina")} #{activeOrders[0].order_number} · {activeOrders[0].restaurant_name}</span>
            <ChevronRight size={18} />
          </button>
        )}

        <main className="px-5 mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Store size={20} className="text-brand-red" />
            <h2 className="font-display font-black text-2xl flex-1 min-w-0">{t("home.restaurants", "Restorani")}</h2>
            <button data-testid="nearest-toggle" type="button" onClick={toggleNearest} disabled={locating}
              aria-pressed={nearestMode}
              className={`shrink-0 text-xs font-black uppercase tracking-wide px-3 py-2 rounded-full border-2 transition-colors disabled:opacity-60 ${
                nearestMode ? "bg-brand-red text-white border-brand-red" : "bg-white text-slate-600 border-slate-200"}`}>
              {locating ? "..." : t("home.nearest", "Najbliže")}
            </button>
          </div>
          {nearestMode && (
            <p data-testid="nearest-active-hint" className="text-xs font-semibold text-slate-400 -mt-2 mb-3">
              {t("home.nearest_on", "Sortirano po udaljenosti")}
            </p>
          )}

          <div className="space-y-5">
            {filteredList.length === 0 && (
              <p className="text-slate-400 text-center py-10" data-testid="no-results">
                {q ? t("home.no_results", "Nema rezultata.") : t("home.none_available", "Trenutno nema dostupnih restorana.")}
              </p>
            )}
            {filteredList.map((r, i) => (
              <RestaurantCard key={r.id} r={r} onOpen={openRestaurant} priority={i < criticalCount ? i + 1 : 0}
                cardRef={(el) => registerCard(el, r)} loyalty={loyaltyByRestaurant[r.id]}
                reorderOrderId={reorderMap[r.id]} onQuickReorder={quickReorder} reordering={reorderingId === r.id} />
            ))}
          </div>
        </main>

        {replaceAsk && (
          <BottomSheet open={!!replaceAsk} onClose={() => setReplaceAsk(null)} testId="quick-reorder-replace"
            zIndex={60} maxHeight="max-h-[80vh]" dismissible={false} label={t("qr.replace_confirm", "Zameni korpu")}>
            <div className="p-6 overflow-y-auto overscroll-contain">
              <p className="text-slate-700">
                {replaceAsk.sameRest
                  ? t("qr.replace_same", "Već imate proizvode u korpi. Da li želite da ih zamenite prethodnom porudžbinom?")
                  : t("qr.replace_other", "Vaša korpa sadrži proizvode iz drugog restorana. Da li želite da je zamenite prethodnom porudžbinom?")}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button data-testid="qr-replace-cancel" onClick={() => setReplaceAsk(null)} className="rounded-full py-3 bg-slate-100 font-bold">{t("common.cancel", "Otkaži")}</button>
                <button data-testid="qr-replace-confirm" onClick={() => { const a = replaceAsk; setReplaceAsk(null); runQuickReorder(a.r, a.orderId); }}
                  className="rounded-full py-3 bg-brand-red text-white font-bold">{t("qr.replace_confirm", "Zameni korpu")}</button>
              </div>
            </div>
          </BottomSheet>
        )}

        <footer className="px-5 mt-8 pb-8 text-center" data-testid="app-footer">
          <div className="flex items-center justify-center gap-1.5 text-slate-400"><FooterLogo height={24} /><span className="font-display font-bold text-slate-500">Ponesi</span></div>
          <p className="text-xs text-slate-400 mt-1">{t("home.tagline", "Poruči. Ponesi. Uživaj.")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-400">
            <a href="/kontakt" data-testid="footer-contact" className="hover:text-brand-red">{t("acct.contact", "Kontakt")}</a><span>·</span>
            <a href="/b2b" data-testid="footer-b2b" className="hover:text-brand-red">Ponesi B2B</a><span>·</span>
            <a href={locale === "en" ? "/en/privacy" : "/politika-privatnosti"} data-testid="footer-privacy" className="hover:text-brand-red">{t("acct.privacy", "Politika privatnosti")}</a><span>·</span>
            <a href={locale === "en" ? "/en/terms" : "/uslovi-koriscenja"} data-testid="footer-terms" className="hover:text-brand-red">{t("acct.terms", "Uslovi korišćenja")}</a>
          </div>
        </footer>

        {accountOpen && user && (
          <BottomSheet open={accountOpen} onClose={() => setAccountOpen(false)} testId="account-sheet"
            maxHeight="max-h-[88vh]" label={t("acct.account_section", "Nalog")}>
            <>
              <div className="flex items-center justify-between p-6 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3 min-w-0">
                  {user.picture && <img src={user.picture} className="w-12 h-12 rounded-full shrink-0" alt="" />}
                  <div className="min-w-0"><p className="font-display font-bold text-lg truncate">{user.name}</p><p className="text-sm text-muted-foreground truncate">{user.email}</p></div>
                </div>
                <button data-testid="account-close" aria-label={t("common.close", "Zatvori")} onClick={() => setAccountOpen(false)} className="bg-slate-100 rounded-full p-2 shrink-0"><X size={18} /></button>
              </div>

              <div className="overflow-y-auto overscroll-contain p-6 pt-4">
                <section data-testid="account-section">
                  <SectionLabel>{t("acct.account_section", "Nalog")}</SectionLabel>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3" data-testid="phone-setting">
                    <label className="font-semibold text-sm">{t("acct.phone", "Broj telefona")}</label>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("acct.phone_hint", "Restoran vas kontaktira na ovaj broj za nove porudžbine.")}</p>
                    <div className="flex gap-2 mt-2">
                      <input data-testid="phone-input" type="tel" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+381 6X XXX XXXX"
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white outline-none focus:border-brand-red" />
                      <button data-testid="phone-save-btn" onClick={savePhone} disabled={savingPhone}
                        className="rounded-xl px-4 py-2 text-sm font-bold bg-brand-red text-white disabled:opacity-50">{t("acct.save", "Sačuvaj")}</button>
                    </div>
                  </div>
                  <button data-testid="my-orders-btn" onClick={() => { setAccountOpen(false); setOrdersOpen(true); }} className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.my_orders", "Moje porudžbine")} <ChevronRight size={18} /></button>
                  <button data-testid="my-coins-btn" onClick={() => { setAccountOpen(false); setCoinsOpen(true); }} className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">
                    <span className="flex items-center gap-2"><PonesiCoinIcon size={20} /> {t("coins.my", "Moji Ponesi Coins")}</span> <ChevronRight size={18} />
                  </button>
                </section>

                <section data-testid="settings-section" className="mt-5">
                  <SectionLabel>{t("acct.settings_section", "Podešavanja")}</SectionLabel>
                  <CitySelector variant="row" />
                  <div className="mt-2 bg-slate-100 rounded-2xl px-4 py-3" data-testid="language-setting">
                    <p className="font-semibold mb-2">{t("acct.language", "Jezik")}</p>
                    <div className="flex gap-2">
                      <button data-testid="lang-sr" onClick={() => switchLocale("sr")}
                        className={`flex-1 rounded-xl py-2 text-sm font-bold ${locale === "sr" ? "bg-brand-red text-white" : "bg-white text-slate-600"}`}>{t("acct.lang_sr", "Srpski")}</button>
                      <button data-testid="lang-en" onClick={() => switchLocale("en")}
                        className={`flex-1 rounded-xl py-2 text-sm font-bold ${locale === "en" ? "bg-brand-red text-white" : "bg-white text-slate-600"}`}>{t("acct.lang_en", "English")}</button>
                    </div>
                  </div>
                  <PushToggle />
                  {installMode !== "installed" && installMode !== "none" && (
                    <button data-testid="install-app-btn" onClick={() => { setAccountOpen(false); openInstallPrompt(); }}
                      className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">
                      <span className="flex items-center gap-2"><Download size={18} className="text-brand-red" /> {t("acct.install", "Instaliraj Ponesi")}</span> <ChevronRight size={18} />
                    </button>
                  )}
                </section>

                {(user.memberships?.length > 0 || user.role === "admin") && (
                  <section data-testid="access-section" className="mt-5">
                    <SectionLabel>{t("acct.access_section", "Pristup")}</SectionLabel>
                    {(user.memberships?.length > 0 || user.role === "admin") && <a href="/restaurant" data-testid="restaurant-panel-link" className="w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.restaurant_panel", "Restoran panel")} <ChevronRight size={18} /></a>}
                    {user.role === "admin" && <a href="/admin" data-testid="admin-panel-link" className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold">{t("acct.admin_panel", "Admin panel")} <ChevronRight size={18} /></a>}
                  </section>
                )}

                <button data-testid="logout-btn" onClick={async () => { await logout(); setAccountOpen(false); toast.success(t("acct.logout_toast", "Odjavljeni ste")); }} className="mt-5 w-full bg-brand-red/10 text-brand-red rounded-2xl px-4 py-3.5 font-bold">{t("acct.logout", "Odjava")}</button>
              </div>
            </>
          </BottomSheet>
        )}

        {ordersOpen && (
          <BottomSheet open={ordersOpen} onClose={() => setOrdersOpen(false)} testId="orders-sheet"
            maxHeight="max-h-[80vh]" label={t("acct.my_orders", "Moje porudžbine")}>
            <div className="p-6 overflow-y-auto overscroll-contain">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-black text-xl">{t("acct.my_orders", "Moje porudžbine")}</h3>
                <button data-testid="orders-close" onClick={() => setOrdersOpen(false)}><X size={20} /></button>
              </div>
              {myOrders.length === 0 && <p className="text-muted-foreground">{t("acct.no_orders", "Još nemate porudžbina.")}</p>}
              {(() => {
                const active = myOrders.filter((o) => ["new", "time_change_pending", "accepted", "preparing", "ready"].includes(o.status));
                const history = myOrders.filter((o) => ["completed", "cancelled", "rejected"].includes(o.status)).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                const label = { completed: t("order.picked_up", "Preuzeto"), cancelled: t("order.cancelled", "Otkazano"), rejected: t("order.rejected", "Odbijeno"), ready: t("status.ready", "Spremno"), preparing: t("status.preparing", "Priprema se"), new: t("status.new", "Poslato"), accepted: t("status.accepted", "Prihvaćeno"), time_change_pending: t("status.new", "Poslato") };
                const Card = (o, now) => (
                  <button key={o.id} data-testid={`order-card-${o.order_number}`} onClick={() => {
                    setOrdersOpen(false);
                    // Terminal orders open in HISTORY mode (stable URL marker, survives refresh/copy)
                    // so a completed order shows its details instead of redirecting home.
                    const terminal = ["completed", "cancelled", "rejected", "expired"].includes(o.status);
                    navigate(localePath(`/order/${o.id}${terminal ? "?view=history" : ""}`));
                  }} className="w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3">
                    <div className="text-left"><p className="font-bold">#{o.order_number} · {o.restaurant_name}</p><p className="text-xs text-muted-foreground">{fmtDateTime(o.created_at, locale)}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {(o.items?.length ?? 0)} {t("acct.items", "stavki")} ·{" "}
                        {o.payment_method === COIN_PM
                          ? <CoinAmount value={o.coins_redeemed} size={13} locale={locale} className="text-amber-700 font-bold" />
                          : RSD(o.total)}
                      </p>
                      {o.payment_method === COIN_PM && (
                        <p data-testid={`order-coin-label-${o.order_number}`} className="text-[11px] font-semibold text-amber-700">{t("coins.paid_with", "Plaćeno Ponesi Coinsima")}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className={`text-xs font-bold uppercase ${o.status === "completed" ? "text-brand-green" : ["cancelled", "rejected"].includes(o.status) ? "text-slate-400" : "text-brand-red"}`}>{label[o.status] || o.status}</span>
                      {now !== undefined && <CompactRemaining order={o} now={now} />}
                    </div>
                  </button>
                );
                return (
                  <div className="space-y-5">
                    {active.length > 0 && (
                      <div data-testid="active-orders-section">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{t("acct.active_orders", "Aktivne porudžbine")}</div>
                        <div className="space-y-2"><ActiveOrdersList orders={active} Card={Card} /></div>
                      </div>
                    )}
                    {history.length > 0 && (
                      <div data-testid="history-orders-section">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">{t("acct.order_history", "Istorija porudžbina")}</div>
                        <div className="space-y-2">{history.map((o) => Card(o))}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </BottomSheet>
        )}

        {coinsOpen && (
          <Suspense fallback={null}>
            <MyCoinsSheet open={coinsOpen} onClose={() => setCoinsOpen(false)} />
          </Suspense>
        )}

        <CustomerBottomNav t={t}
          active={ordersOpen ? "orders" : accountOpen ? "profile" : "home"}
          onHome={() => { setOrdersOpen(false); setAccountOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          onSearch={() => {
            setOrdersOpen(false); setAccountOpen(false);
            searchRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
            searchRef.current?.focus();
          }}
          onOrders={() => { setAccountOpen(false); setOrdersOpen(true); }}
          onProfile={openProfile} />
      </div>
    </div>
  );
}
